import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles, CheckCircle2, TrendingUp, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const RECOMMENDATION_LABEL = {
  ready: { label: "Interview Ready", classes: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  needs_practice: { label: "Needs More Practice", classes: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
  not_ready: { label: "Not Interview Ready", classes: "text-rose-300 border-rose-500/30 bg-rose-500/10" },
};

function ScoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const col = v >= 80 ? "bg-emerald-400" : v >= 60 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-100 font-semibold tabular-nums">{v}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full ${col} rounded-full transition-all`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

export default function PublicAssessment() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backend}/api/video-interview/public/${shareId}`);
        if (res.status === 410) {
          setErr("This share link has been revoked by its owner.");
          return;
        }
        if (!res.ok) {
          setErr("Assessment not found or no longer available.");
          return;
        }
        setData(await res.json());
      } catch {
        setErr("Failed to load assessment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center text-zinc-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading Assessment…
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex flex-col items-center justify-center text-center px-6">
        <AlertCircle className="h-10 w-10 text-zinc-600 mb-4" />
        <div className="text-lg font-semibold text-zinc-100">Assessment unavailable</div>
        <div className="text-sm text-zinc-500 mt-2 max-w-sm">{err || "The link may have been removed."}</div>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200"
        >
          Go to Intuiterview <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  const r = data.report || {};
  const rec = RECOMMENDATION_LABEL[r.hiring_recommendation] || RECOMMENDATION_LABEL.needs_practice;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-100" data-testid="public-assessment-page">
      {/* Top bar */}
      <header className="border-b border-zinc-800/70 bg-zinc-950/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo variant="lockup" iconSize={30} to="/" testid="public-assess-brand" />
          <Link
            to="/"
            data-testid="public-assess-cta-signup"
            className="inline-flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-3.5 py-2 shadow-[0_0_20px_-6px_rgba(245,158,11,0.55)]"
          >
            Get your own Assessment <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Hiring Assessment · Public share
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="text-2xl font-bold text-zinc-50 tracking-tight">
            {data.role || "Interview"}
            {data.company ? <span className="text-zinc-500"> · {data.company}</span> : null}
          </h1>
          <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-xs font-semibold ${rec.classes}`}>
            ● {rec.label}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/40 to-zinc-950/60 p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Overall Interview Score</div>
            <div className="mt-1 text-3xl font-bold">
              {r.overall_score ?? "–"}<span className="text-base text-zinc-500">/100</span>
            </div>
            {r.level && (
              <div className="text-[10px] uppercase tracking-widest text-amber-400/80 mt-1">{r.level}</div>
            )}
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Official Readiness Score</div>
            <div className="mt-1 text-3xl font-bold">
              {r.readiness_score ?? "–"}<span className="text-base text-zinc-500">/100</span>
            </div>
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Speech Pace</div>
            <div className="mt-1 text-3xl font-bold">
              {r.speech_pace_wpm ?? 0}<span className="text-base text-zinc-500"> wpm</span>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">{r.filler_words_count ?? 0} filler words</div>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-sm font-semibold text-zinc-50 mb-3">Competency Breakdown</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
            <ScoreBar label="Communication" value={r.communication} />
            <ScoreBar label="Confidence" value={r.confidence} />
            <ScoreBar label="Answer Structure" value={r.answer_structure} />
            <ScoreBar label="Job Fit" value={r.job_fit} />
            <ScoreBar label="Leadership Presence" value={r.leadership_presence} />
            <ScoreBar label="Executive Presence" value={r.executive_presence} />
            <ScoreBar label="STAR Framework" value={r.star_score} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Strengths</div>
            <ul className="space-y-1.5 text-xs text-zinc-200">
              {(r.strengths || []).map((s, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />{s}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.03] p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Improvement Areas</div>
            <ul className="space-y-1.5 text-xs text-zinc-200">
              {(r.improvements || []).map((s, i) => (
                <li key={i} className="flex gap-2">
                  <TrendingUp className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />{s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {r.coaching && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Personalized Coaching</div>
            <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{r.coaching}</p>
          </div>
        )}

        {(r.next_steps || []).length > 0 && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Next Steps</div>
            <ul className="space-y-1.5 text-xs text-zinc-200">
              {r.next_steps.map((s, i) => (
                <li key={i} className="flex gap-2"><span className="text-amber-400 mt-0.5">→</span>{s}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer conversion CTA */}
        <div className="mt-4 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/50 to-zinc-950/60 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1">Your turn</div>
            <div className="text-lg font-semibold text-zinc-50 leading-tight">
              Run a Official AI Interview and receive your own Hiring Assessment.
            </div>
            <div className="text-xs text-zinc-500 mt-1">10-min interview · Instant executive-grade report.</div>
          </div>
          <Link
            to="/"
            data-testid="public-assess-footer-cta"
            className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] flex-shrink-0"
          >
            Start free on Intuiterview <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="pt-2 text-[10px] text-zinc-600 tracking-wide uppercase text-center">
          {data.redact_pii ? "Shared with PII redaction" : "Shared without redaction"} · Powered by Intuiterview
        </div>
      </main>
    </div>
  );
}
